import React from 'react';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import SubjectIcon from '@mui/icons-material/Subject';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';

const TestDisplay = ({ tests, onTestClick, view, setView }) => {
  const theme = useTheme();
  const CardView = () => (    
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { 
        xs: '1fr', 
        sm: 'repeat(2, 1fr)', 
        md: 'repeat(2, 1fr)', 
        lg: 'repeat(3, 1fr)' 
      }, 
      gap: { xs: 2, sm: 2.5, md: 3 }
    }}>
      {tests.map((test) => (
        <Card
          key={test.id}
          sx={{
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { boxShadow: 8, transform: 'scale(1.03)' },
            background: theme.palette.mode === 'light' 
              ? 'linear-gradient(135deg, #f5f7fa 0%, #e1e1e1 100%)'
              : 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)',
            borderRadius: 3,
            height: '100%',
            minHeight: { xs: 200, sm: 220 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
          onClick={() => onTestClick(test)}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <AccessTimeIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="text.secondary">
                {test.duration} min
              </Typography>
            </Stack>
            <Typography variant="h6" color="primary" gutterBottom noWrap>
              {test.name}
            </Typography>
            <Stack direction="row" spacing={1} mb={1}>
              {test.exam_master && (
                <Chip icon={<SchoolIcon />} label={test.exam_master.name} size="small" color="primary" />
              )}
              {test.subject_master && (
                <Chip icon={<SubjectIcon />} label={test.subject_master.name} size="small" color="secondary" />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Code: <span style={{ fontFamily: 'monospace' }}>{test.code}</span>
            </Typography>
            <Button
              variant="outlined"
              size="small"
              endIcon={<PlayArrowIcon />}
              sx={{ 
                mt: 1, 
                borderRadius: 2, 
                textTransform: 'none', 
                borderColor: 'primary.main', 
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : undefined
                }
              }}
              onClick={e => { e.stopPropagation(); onTestClick(test); }}
              
            >
              Start Test
            </Button>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const ListView = () => (
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {tests.map((test, index) => (
            <React.Fragment key={test.id}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Typography variant="subtitle1" color="primary" component="span">
                        {test.name}
                      </Typography>
                      {test.exam_master && (
                        <Chip
                          icon={<SchoolIcon />}
                          label={test.exam_master.name}
                          size="small"
                          color="primary"
                        />
                      )}
                      {test.subject_master && (
                        <Chip
                          icon={<SubjectIcon />}
                          label={test.subject_master.name}
                          size="small"
                          color="secondary"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" component="span">
                          {test.duration} min
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" component="span">
                        Code: <span style={{ fontFamily: 'monospace' }}>{test.code}</span>
                      </Typography>
                    </Box>
                  }
                />
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<PlayArrowIcon />}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none', 
                    borderColor: 'primary.main', 
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : undefined
                    }
                  }}
                  onClick={() => onTestClick(test)}
                >
                  Start
                </Button>
              </ListItem>
              {index < tests.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
  );

  return (
    <Box width="100%" mx="auto">
      {view === 'grid' ? <CardView /> : <ListView />}
    </Box>
  );
};

export default TestDisplay; 